import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { listarParametrosPublicos, registrarUsuario, type Parametro } from '../lib';
import { useAuth } from '../context/AuthContext';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
}

type NavigatorWithStandalone = Navigator & {
  standalone?: boolean;
};

const DEFAULT_BRANDING = {
  systemName: 'Acceso al sistema',
  systemSlogan: 'Ingresa con tus credenciales para continuar con la operación.',
  logoUrl: '',
};

const EXECUTIVE_HIGHLIGHTS = [
  {
    title: 'Gestión segura',
    detail: 'Gestión segura de usuarios, conductores y vendedores desde un solo punto de acceso.',
  },
  {
    title: 'Entregas monitorizadas',
    detail: 'Seguimiento operativo con entregas monitorizadas en tiempo real para una mejor coordinación.',
  },
  {
    title: 'Registro completo',
    detail: 'Captura de datos completos de entregas, responsables, estados y trazabilidad operativa.',
  },
];

function readParametroValue(parametros: Parametro[], slug: string): string {
  const parametro = parametros.find(item => item.slug === slug);
  return parametro?.valor?.trim() ?? '';
}

function resolveBranding(parametros: Parametro[]) {
  return {
    systemName: readParametroValue(parametros, 'sistema_nombre') || DEFAULT_BRANDING.systemName,
    systemSlogan: readParametroValue(parametros, 'sistema_slogan') || DEFAULT_BRANDING.systemSlogan,
    logoUrl: readParametroValue(parametros, 'sistema_logo_url'),
  };
}

function getInitials(value: string): string {
  const initials = value
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word[0]?.toUpperCase() ?? '')
    .join('');

  return initials || 'GS';
}

function PasswordField({
  value,
  onChange,
  placeholder,
  className,
  name,
  required,
  minLength,
  autoComplete,
}: {
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  className: string;
  name?: string;
  required?: boolean;
  minLength?: number;
  autoComplete?: string;
}) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="relative">
      <input
        name={name}
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        required={required}
        minLength={minLength}
        autoComplete={autoComplete}
        className={`${className} pr-24`}
        placeholder={placeholder}
      />
      <button
        type="button"
        onClick={() => setVisible(current => !current)}
        className="absolute inset-y-1.5 right-2 rounded-xl px-3 text-[10px] font-semibold uppercase tracking-[0.24em] text-stone-500 transition duration-200 hover:bg-stone-100 hover:text-[#d42a2a]"
        aria-label={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
        title={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
      >
        {visible ? 'Ocultar' : 'Mostrar'}
      </button>
    </div>
  );
}

export default function LoginPage() {
  const { login, user } = useAuth();
  const [view, setView] = useState<'login' | 'registro-usuario'>('login');
  const [mobilePanel, setMobilePanel] = useState<'acceso' | 'identidad'>('acceso');
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [logoVisible, setLogoVisible] = useState(true);
  const [deferredInstallPrompt, setDeferredInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installingApp, setInstallingApp] = useState(false);
  const [installFeedback, setInstallFeedback] = useState('');
  const [iosInstallAvailable, setIosInstallAvailable] = useState(false);
  const [appInstalled, setAppInstalled] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const [registerSuccess, setRegisterSuccess] = useState('');
  const [registerForm, setRegisterForm] = useState({
    username: '',
    email: '',
    password: '',
    nombre: '',
    telefono: '',
  });

  useEffect(() => {
    let active = true;

    async function loadBranding() {
      try {
        const response = await listarParametrosPublicos();
        if (!active) return;

        const parametros = Array.isArray(response.data) ? response.data : [];
        setBranding(resolveBranding(parametros));
      } catch {
        if (active) {
          setBranding(DEFAULT_BRANDING);
        }
      }
    }

    void loadBranding();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    setLogoVisible(true);
  }, [branding.logoUrl]);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    document.title = branding.systemName === DEFAULT_BRANDING.systemName
      ? 'Portal del sistema'
      : `${branding.systemName} · Acceso`;
  }, [branding.systemName]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const navigatorWithStandalone = window.navigator as NavigatorWithStandalone;
    const displayMode = window.matchMedia('(display-mode: standalone)');

    const syncInstallState = () => {
      const installed = displayMode.matches || navigatorWithStandalone.standalone === true;
      const isIos = /iphone|ipad|ipod/i.test(window.navigator.userAgent);
      setAppInstalled(installed);
      setIosInstallAvailable(isIos && !installed);
    };

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setDeferredInstallPrompt(event as BeforeInstallPromptEvent);
      setInstallFeedback('');
    };

    const handleAppInstalled = () => {
      setAppInstalled(true);
      setDeferredInstallPrompt(null);
      setInstallFeedback('Aplicación instalada correctamente.');
    };

    syncInstallState();

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener);
    window.addEventListener('appinstalled', handleAppInstalled);
    displayMode.addEventListener?.('change', syncInstallState);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener);
      window.removeEventListener('appinstalled', handleAppInstalled);
      displayMode.removeEventListener?.('change', syncInstallState);
    };
  }, []);

  if (user) return null;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(username, password);
    } catch (err: unknown) {
      const msg =
        (err as { message?: string })?.message ??
        'Credenciales incorrectas. Inténtelo de nuevo.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  function handleRegisterField(event: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target;
    setRegisterForm(prev => ({ ...prev, [name]: value }));
  }

  async function handleRegisterSubmit(event: FormEvent) {
    event.preventDefault();
    setRegisterError('');
    setRegisterSuccess('');
    setRegisterLoading(true);

    try {
      await registrarUsuario({
        username: registerForm.username.trim(),
        email: registerForm.email.trim(),
        password: registerForm.password,
        rol: 'CONDUCTOR',
        nombre: registerForm.nombre.trim(),
        telefono: registerForm.telefono.trim(),
      });

      setRegisterSuccess('Registro completado. Ya puedes iniciar sesión con tu usuario.');
      setRegisterForm({
        username: '',
        email: '',
        password: '',
        nombre: '',
        telefono: '',
      });
      setView('login');
      setMobilePanel('acceso');
    } catch (err: unknown) {
      setRegisterError((err as { message?: string })?.message ?? 'No se pudo registrar el usuario.');
    } finally {
      setRegisterLoading(false);
    }
  }

  async function handleInstallApp() {
    if (appInstalled) {
      setInstallFeedback('La aplicación ya está instalada en este dispositivo.');
      return;
    }

    if (deferredInstallPrompt) {
      try {
        setInstallingApp(true);
        setInstallFeedback('');
        await deferredInstallPrompt.prompt();
        const choice = await deferredInstallPrompt.userChoice;
        setInstallFeedback(
          choice.outcome === 'accepted'
            ? 'Aceptaste la instalación. El navegador completará el proceso en unos segundos.'
            : 'La instalación fue cancelada.'
        );
        setDeferredInstallPrompt(null);
      } catch {
        setInstallFeedback('No se pudo abrir la instalación en este navegador.');
      } finally {
        setInstallingApp(false);
      }
      return;
    }

    if (iosInstallAvailable) {
      setInstallFeedback('En iPhone o iPad, toca Compartir y luego Añadir a pantalla de inicio.');
      return;
    }

    setInstallFeedback('La instalación estará disponible cuando el navegador detecte esta aplicación como instalable.');
  }

  const isLoginView = view === 'login';
  const showLogo = Boolean(branding.logoUrl) && logoVisible;
  const inputCls = 'w-full rounded-2xl border border-stone-200 bg-white px-4 py-3 text-sm text-stone-900 shadow-[0_16px_35px_rgba(15,23,42,0.06)] outline-none transition duration-200 placeholder:text-stone-400 focus:border-[#d42a2a] focus:ring-4 focus:ring-red-100';
  const labelCls = 'mb-2 block text-[11px] font-semibold uppercase tracking-[0.24em] text-stone-500';
  const primaryButtonCls = 'inline-flex w-full transform-gpu items-center justify-center rounded-2xl bg-[#d42a2a] px-4 py-3 text-sm font-semibold text-white shadow-[0_20px_40px_rgba(212,42,42,0.28)] transition duration-200 ease-out hover:-translate-y-0.5 hover:bg-[#a81d1d] hover:shadow-[0_24px_44px_rgba(168,29,29,0.32)] disabled:translate-y-0 disabled:cursor-not-allowed disabled:opacity-60';
  const secondaryButtonCls = 'inline-flex w-full transform-gpu items-center justify-center rounded-2xl border border-stone-200 bg-white px-4 py-3 text-sm font-semibold text-stone-700 shadow-[0_14px_28px_rgba(15,23,42,0.05)] transition duration-200 ease-out hover:-translate-y-0.5 hover:bg-stone-50 hover:shadow-[0_18px_34px_rgba(15,23,42,0.08)]';
  const installButtonCls = 'inline-flex w-full transform-gpu items-center justify-center rounded-2xl border border-[#d42a2a]/20 bg-red-50 px-4 py-3 text-sm font-semibold text-[#a81d1d] shadow-[0_16px_30px_rgba(212,42,42,0.10)] transition duration-200 ease-out hover:-translate-y-0.5 hover:bg-red-100 disabled:translate-y-0 disabled:cursor-not-allowed disabled:opacity-60';

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#1a1a1a]">
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.98) 0%, rgba(52, 14, 14, 0.95) 48%, rgba(15, 23, 42, 0.92) 100%)',
        }}
      />
      <div className="absolute -left-16 top-16 h-72 w-72 rounded-full bg-[#d42a2a]/25 blur-3xl animate-pulse" />
      <div className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-white/10 blur-3xl animate-pulse" />

      <div className="relative mx-auto flex min-h-screen w-full max-w-7xl flex-col gap-3 px-4 py-4 sm:px-6 sm:py-8 lg:px-8 xl:items-center xl:py-10">
        <div className="w-full rounded-[28px] border border-white/12 bg-white/8 p-2 shadow-[0_24px_60px_rgba(15,23,42,0.26)] backdrop-blur xl:hidden">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setMobilePanel('acceso')}
              className={`rounded-[20px] px-4 py-3 text-sm font-semibold transition duration-200 ${mobilePanel === 'acceso' ? 'bg-white text-stone-950 shadow-[0_16px_28px_rgba(15,23,42,0.14)]' : 'text-white/75 hover:bg-white/10 hover:text-white'}`}
            >
              Acceso
            </button>
            <button
              type="button"
              onClick={() => setMobilePanel('identidad')}
              className={`rounded-[20px] px-4 py-3 text-sm font-semibold transition duration-200 ${mobilePanel === 'identidad' ? 'bg-white text-stone-950 shadow-[0_16px_28px_rgba(15,23,42,0.14)]' : 'text-white/75 hover:bg-white/10 hover:text-white'}`}
            >
              Identidad visual
            </button>
          </div>
        </div>

        <div className="grid w-full gap-3 rounded-[36px] border border-white/12 bg-white/7 p-3 shadow-[0_40px_120px_rgba(15,23,42,0.45)] backdrop-blur xl:grid-cols-[1.05fr_0.95fr]">
          <section
            className={`${mobilePanel === 'identidad' ? 'block' : 'hidden'} relative overflow-hidden rounded-[30px] p-8 text-white sm:p-10 xl:block`}
            style={{
              background: 'linear-gradient(160deg, rgba(168, 29, 29, 0.98) 0%, rgba(120, 24, 24, 0.96) 44%, rgba(26, 26, 26, 0.98) 100%)',
            }}
          >
            <div className="absolute inset-0 opacity-25" style={{ background: 'radial-gradient(circle at top right, rgba(255, 255, 255, 0.35), transparent 28%), radial-gradient(circle at bottom left, rgba(255, 255, 255, 0.18), transparent 24%)' }} />

            <div className="relative flex h-full flex-col">
              <div className="inline-flex w-fit rounded-full border border-white/20 bg-white/10 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.28em] text-white/80 backdrop-blur">
                Portal ejecutivo
              </div>

              <div className="mt-8 flex flex-col items-center text-center">
                <div className="flex min-h-32 w-full items-center justify-center rounded-[28px] border border-white/14 bg-white/10 px-6 py-6 shadow-[0_24px_60px_rgba(0,0,0,0.18)] backdrop-blur">
                  {showLogo ? (
                    <img
                      src={branding.logoUrl}
                      alt={branding.systemName}
                      className="max-h-24 w-auto max-w-full object-contain"
                      onError={() => setLogoVisible(false)}
                    />
                  ) : (
                    <span
                      className="text-3xl font-bold tracking-[0.28em] text-white"
                      style={{ fontFamily: 'Montserrat, Inter, sans-serif' }}
                    >
                      {getInitials(branding.systemName)}
                    </span>
                  )}
                </div>

                <div className="mt-6 min-w-0 text-center">
                  <h1
                    className="mt-3 text-3xl font-bold leading-tight text-white sm:text-4xl"
                    style={{ fontFamily: 'Montserrat, Inter, sans-serif' }}
                  >
                    {branding.systemName}
                  </h1>
                  <p className="mt-3 max-w-2xl text-sm leading-7 text-white/78 sm:text-base">
                    {branding.systemSlogan}
                  </p>
                </div>
              </div>

              <div className="mt-10 grid gap-3 sm:grid-cols-3 xl:grid-cols-1">
                {EXECUTIVE_HIGHLIGHTS.map(item => (
                  <div
                    key={item.title}
                    className="rounded-[26px] border border-white/12 bg-white/10 p-5 shadow-[0_18px_45px_rgba(0,0,0,0.14)] transition duration-300 hover:-translate-y-0.5 hover:bg-white/14"
                  >
                    <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-white/65">
                      {item.title}
                    </p>
                    <p className="mt-3 text-sm leading-6 text-white/82">
                      {item.detail}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-8 rounded-[26px] border border-white/12 bg-black/15 p-5 text-sm leading-7 text-white/75 shadow-[0_18px_40px_rgba(0,0,0,0.12)]">
                Plataforma orientada a la gestión segura de usuarios, conductores y vendedores, con control operativo de entregas y registro completo de información clave.
              </div>
            </div>
          </section>

          <section className={`${mobilePanel === 'acceso' ? 'block' : 'hidden'} rounded-[30px] bg-white/96 p-6 shadow-[0_32px_90px_rgba(15,23,42,0.10)] sm:p-8 lg:p-10 xl:block`}>
            <div className="rounded-[28px] border border-stone-200/80 bg-stone-50/80 p-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]">
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setView('login');
                    setMobilePanel('acceso');
                    setError('');
                    setRegisterError('');
                  }}
                  className={`rounded-[20px] px-4 py-3 text-sm font-semibold transition duration-200 ${isLoginView ? 'bg-white text-stone-950 shadow-[0_16px_28px_rgba(15,23,42,0.10)]' : 'text-stone-500 hover:text-stone-900'}`}
                >
                  Ingresar
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setView('registro-usuario');
                    setMobilePanel('acceso');
                    setError('');
                    setRegisterError('');
                  }}
                  className={`rounded-[20px] px-4 py-3 text-sm font-semibold transition duration-200 ${!isLoginView ? 'bg-white text-stone-950 shadow-[0_16px_28px_rgba(15,23,42,0.10)]' : 'text-stone-500 hover:text-stone-900'}`}
                >
                  Registro usuario
                </button>
              </div>
            </div>

            <div className="mt-8">
              <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#d42a2a]">
                {isLoginView ? 'Inicio de sesión' : 'Alta operativa'}
              </p>
              <h2
                className="mt-4 text-3xl font-bold text-stone-950"
                style={{ fontFamily: 'Montserrat, Inter, sans-serif' }}
              >
                {isLoginView ? 'Accede a tu panel' : 'Crear usuario'}
              </h2>
              <p className="mt-3 max-w-2xl text-sm leading-7 text-stone-600">
                {isLoginView
                  ? `Usa tus credenciales para ingresar a ${branding.systemName} con un entorno visual alineado a la identidad del sistema.`
                  : 'Este formulario crea usuarios y los registra por defecto con rol Conductor, sin asociar ni capturar datos de vehículo.'}
              </p>
            </div>

            {registerSuccess && (
              <p className="mt-6 rounded-[22px] border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 shadow-[0_14px_30px_rgba(16,185,129,0.10)]">
                {registerSuccess}
              </p>
            )}

            {isLoginView ? (
              <>
                <form onSubmit={handleSubmit} className="mt-8 space-y-5">
                  <div>
                    <label className={labelCls}>
                      Usuario
                    </label>
                    <input
                      type="text"
                      value={username}
                      onChange={event => setUsername(event.target.value)}
                      required
                      autoFocus
                      className={inputCls}
                      placeholder="Ingresa tu usuario"
                    />
                  </div>

                  <div>
                    <label className={labelCls}>
                      Contraseña
                    </label>
                    <PasswordField
                      value={password}
                      onChange={event => setPassword(event.target.value)}
                      className={inputCls}
                      placeholder="Ingresa tu contraseña"
                      required
                      autoComplete="current-password"
                    />
                  </div>

                  {error && (
                    <p className="rounded-[22px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 shadow-[0_12px_26px_rgba(239,68,68,0.08)]">
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className={primaryButtonCls}
                  >
                    {loading ? 'Iniciando sesión…' : 'Iniciar sesión'}
                  </button>

                  <button
                    type="button"
                    onClick={() => void handleInstallApp()}
                    disabled={installingApp}
                    className={installButtonCls}
                  >
                    {installingApp ? 'Preparando instalación…' : 'Instalar aplicación'}
                  </button>

                  {installFeedback && (
                    <p className="rounded-[22px] border border-stone-200 bg-stone-50 px-4 py-3 text-sm text-stone-600 shadow-[0_12px_24px_rgba(15,23,42,0.05)]">
                      {installFeedback}
                    </p>
                  )}
                </form>

                <div className="mt-6 rounded-[24px] border border-stone-200 bg-stone-50 px-5 py-4 text-sm leading-7 text-stone-600">
                  Acceso habilitado para administradores, vendedores y conductores registrados dentro del tenant activo.
                </div>
              </>
            ) : (
              <form onSubmit={handleRegisterSubmit} className="mt-8 space-y-5">
                <div className="grid gap-4 lg:grid-cols-2">
                  <div>
                    <label className={labelCls}>Usuario</label>
                    <input name="username" value={registerForm.username} onChange={handleRegisterField} required className={inputCls} autoComplete="off" placeholder="Usuario de acceso" />
                  </div>
                  <div>
                    <label className={labelCls}>Correo</label>
                    <input name="email" type="email" value={registerForm.email} onChange={handleRegisterField} required className={inputCls} placeholder="correo@empresa.com" />
                  </div>
                  <div>
                    <label className={labelCls}>Contraseña</label>
                    <PasswordField
                      name="password"
                      value={registerForm.password}
                      onChange={handleRegisterField}
                      required
                      minLength={8}
                      className={inputCls}
                      autoComplete="new-password"
                      placeholder="Mínimo 8 caracteres"
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Nombre completo</label>
                    <input name="nombre" value={registerForm.nombre} onChange={handleRegisterField} required className={inputCls} placeholder="Nombre y apellido" />
                  </div>
                  <div>
                    <label className={labelCls}>Teléfono</label>
                    <input name="telefono" value={registerForm.telefono} onChange={handleRegisterField} required className={inputCls} placeholder="+593 99 999 9999" />
                  </div>
                </div>

                <div className="rounded-[24px] border border-stone-200 bg-stone-50 px-5 py-4 text-sm leading-7 text-stone-600">
                  El registro crea el usuario con rol Conductor por defecto y deja la información del vehículo para la gestión interna posterior.
                </div>

                {registerError && (
                  <p className="rounded-[22px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 shadow-[0_12px_26px_rgba(239,68,68,0.08)]">
                    {registerError}
                  </p>
                )}

                <div className="flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={() => {
                      setView('login');
                      setMobilePanel('acceso');
                      setRegisterError('');
                    }}
                    className={secondaryButtonCls}
                  >
                    Volver al login
                  </button>
                  <button
                    type="submit"
                    disabled={registerLoading}
                    className={primaryButtonCls}
                  >
                    {registerLoading ? 'Registrando…' : 'Crear usuario'}
                  </button>
                </div>
              </form>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
